import { makeStyles } from "tss-react/mui";
import { MiChip, MiPopper } from "@miplatform/components";
import { formatDate } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import { IPriceLevelPayload } from "../PricingMatrix.types";

export function PriceLevelInfo({ anchorEl, level }: PriceLevelInfoProps) {
  const { classes, theme } = useStyles();

  const {
    parentPriceLevel,
    descendentPriceLevels,
    parentId,
    name,
    isActive,
    assignedAccounts = [],
    modifiedOn,
    percentAdjustment = 0,
  } = level || {};

  const priceLevelNames = parentId
    ? parentPriceLevel?.name
    : descendentPriceLevels?.map((pl) => pl.name).join(", ");

  const getPercentageText = () => {
    if (!parentId) {
      return null;
    }
    if (percentAdjustment > 0) {
      return `(+${percentAdjustment}%)`;
    }
    return `(${percentAdjustment}%)`;
  };

  return (
    <MiPopper
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      placement="top"
      sx={{
        background: theme.palette.common.white,
        boxShadow: theme.shadows[1],
        borderRadius: 1,
        width: 190,
      }}
    >
      <Box className={classes.popperHeader}>
        <Typography className={classes.popperTextBold}>{name}</Typography>
        <MiChip
          label={isActive ? "Active" : "Inactive"}
          size="small"
          className={isActive ? classes.activeChip : classes.inactiveChip}
        />
      </Box>
      <Box className={classes.popperBody}>
        {assignedAccounts.length > 0 && (
          <Box className={classes.flexBox}>
            <Typography className={classes.popperTextBold}>
              Customers:
            </Typography>
            <Typography className={classes.popperText}>
              {assignedAccounts.map(({ name }) => name).join(", ")}
            </Typography>
          </Box>
        )}
        {priceLevelNames && (
          <Box className={classes.flexBox}>
            <Typography className={classes.popperTextBold}>
              {parentId ? "Based On: " : "Dependents: "}
            </Typography>
            <Typography className={classes.popperText}>
              {priceLevelNames}
              {getPercentageText()}
            </Typography>
          </Box>
        )}
        <Box className={classes.flexBox}>
          <Typography className={classes.popperTextBold}>
            Last Updated:
          </Typography>
          <Typography className={classes.popperText}>
            {formatDate(modifiedOn, "MM/DD/YYYY")}
          </Typography>
        </Box>
      </Box>
    </MiPopper>
  );
}

const useStyles = makeStyles()((theme) => ({
  popperHeader: {
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    display: "flex",
    backgroundColor: theme.palette.grey.accent,
    justifyContent: "space-between",
    padding: theme.spacing(1),
  },
  popperBody: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    gap: theme.spacing(1),
  },
  popperTextBold: {
    fontSize: theme.fontSize.medium,
    fontWeight: theme.fontWeight.semiBold,
  },
  popperText: {
    fontSize: theme.fontSize.medium,
  },
  activeChip: {
    border: `1px solid ${theme.palette.green.main}`,
    backgroundColor: theme.palette.green.medium,
    borderRadius: 4,
    height: 20,
    color: theme.palette.common.white,
  },
  inactiveChip: {
    border: `1px solid ${theme.palette.red.main}`,
    backgroundColor: theme.palette.red.medium,
    borderRadius: 4,
    height: 20,
    color: theme.palette.common.white,
  },
  flexBox: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

interface PriceLevelInfoProps {
  anchorEl: HTMLElement | null;
  level: IPriceLevelPayload | null;
}
